import { Star } from 'lucide-react';
import { PageHead } from 'ui/kit/PageHead';
import { useState } from 'react';
import { isAxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';

import { api } from 'api/v1/api';
import { FadeIn, StepBody } from 'ui/kit/uiMotion';
import {
  REVIEW_COMMENT_MAX_LENGTH,
  validateReviewCommentText,
} from 'modules/ChatV2/utils/contactFilter';
import { chatKeys } from 'modules/ChatV2/api/queries';
import { parsePositiveInt } from 'utils/parsePositiveInt';

import s4 from './s4Screen.module.scss';
import classes from './StudentReview.module.scss';

export const StudentReviewPage = () => {
  const { tutorUserId } = useParams();
  const tutorId = parsePositiveInt(tutorUserId) ?? 0;

  if (!Number.isFinite(tutorId) || tutorId <= 0) {
    return (
      <main className={s4.page}>
        <p className={s4.body}>Ссылка на отзыв не открылась. Вернись в чат и попробуй еще раз.</p>
      </main>
    );
  }

  return <StudentReviewForm tutorUserId={tutorId} />;
};

function StudentReviewForm({ tutorUserId }: { tutorUserId: number }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [commentError, setCommentError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const mutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/reviews', {
        tutor_user_id: tutorUserId,
        rating,
        comment: comment.trim() || null,
      });
      return response.data as { status_message?: string | null };
    },
    onSuccess: () => {
      setSent(true);
      void queryClient.invalidateQueries({ queryKey: chatKeys.all });
    },
  });

  if (sent) {
    return (
      <FadeIn>
        <StepBody stepKey='review-pending'>
          <main className={s4.page}>
            <PageHead crumb={<>Личный кабинет / Мои задачи</>} title={<>Отзыв на проверке</>} />
            <section className={s4.card}>
              <h2 className={s4.cardTitle}>Отзыв отправлен на проверку</h2>
              <span className={s4.chip}>На проверке</span>
              {rating > 0 ? (
                <>
                  <p className={s4.label}>Оценка</p>
                  <p className={s4.body}>
                    {rating} из 5
                  </p>
                </>
              ) : null}
              {comment.trim() ? <p className={s4.body}>{comment.trim()}</p> : null}
              <p className={s4.hint}>
                Отзыв еще не опубликован. Результат модерации появится в уведомлениях.
              </p>
              <div className={s4.actions}>
                <button type='button' className={s4.primary} onClick={() => navigate('/profile/chats')}>
                  К договоренности
                </button>
                <button
                  type='button'
                  className={s4.secondary}
                  onClick={() => navigate('/profile/notifications')}
                >
                  Уведомления
                </button>
              </div>
            </section>
          </main>
        </StepBody>
      </FadeIn>
    );
  }

  const submitError = mutation.isError
    ? axiosDetail(mutation.error) || 'Не удалось отправить отзыв. Попробуй еще раз.'
    : null;

  const handleSubmit = () => {
    if (rating < 1) {
      setCommentError('Выбери, сколько звезд поставить за занятие.');
      return;
    }
    const error = validateReviewCommentText(comment.trim());
    if (error) {
      setCommentError(error);
      return;
    }
    setCommentError(null);
    mutation.mutate();
  };

  return (
    <FadeIn>
      <main className={s4.page}>
        <PageHead crumb={<>Личный кабинет / Мои задачи</>} title={<>Отзыв о репетиторе</>} />
        <form
          className={s4.card}
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit();
          }}
        >
          <h2 className={s4.cardTitle}>Твой опыт занятия</h2>
          <p className={s4.body}>Заполняй отзыв только о занятии, которое состоялось.</p>
          <p className={s4.label}>Оценка</p>
          <div className={classes.stars} role='radiogroup' aria-label='Звезды за занятие'>
            {[1, 2, 3, 4, 5].map((value) => {
              const on = value <= rating;
              return (
                <button
                  key={value}
                  type='button'
                  role='radio'
                  aria-checked={rating === value}
                  aria-label={`${value} из 5`}
                  className={classes.star}
                  onClick={() => {
                    setRating(value);
                    if (commentError === 'Выбери, сколько звезд поставить за занятие.') {
                      setCommentError(null);
                    }
                  }}
                >
                  <Star size={28} aria-hidden fill={on ? '#f5a623' : 'none'} color={on ? '#f5a623' : '#c4c4c4'} />
                </button>
              );
            })}
          </div>
          <p className={s4.muted}>{rating === 0 ? 'Пока без оценки' : `${rating} из 5`}</p>
          <label className={s4.field}>
            <span className={s4.label}>Текст отзыва</span>
            <textarea
              className={s4.area}
              value={comment}
              rows={5}
              maxLength={REVIEW_COMMENT_MAX_LENGTH}
              aria-invalid={Boolean(commentError)}
              onChange={(event) => {
                const next = event.target.value.slice(0, REVIEW_COMMENT_MAX_LENGTH);
                setComment(next);
                setCommentError(validateReviewCommentText(next.trim()));
              }}
            />
          </label>
          <p className={commentError || submitError ? s4.error : s4.hint}>
            {commentError ||
              submitError ||
              'Не указывай контакты. Отзыв появится после модерации; отправка не означает публикацию.'}
          </p>
          <div className={s4.actions}>
            <button type='submit' className={s4.primary} disabled={mutation.isPending}>
              {mutation.isPending ? 'Отправка...' : 'Отправить на проверку'}
            </button>
            <button type='button' className={s4.secondary} onClick={() => navigate('/profile/chats')}>
              Отмена
            </button>
          </div>
        </form>
      </main>
    </FadeIn>
  );
}

function axiosDetail(error: unknown): string | null {
  if (!isAxiosError(error)) return null;
  const detail = (error.response?.data as { detail?: string } | undefined)?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : null;
}
